import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '../Utils/SessionContext';
import Navigation from './Components/Navigation';
import { API_ENDPOINTS } from '../Utils/ApiConfig';

const Dashboard = () => {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Set document title
  useEffect(() => {
    document.title = "Dashboard";
  }, []);
  
  // Fetch all categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        
        // Check if user exists
        if (!user) {
          console.error('User session not available. Please log in again.');
          return;
        }
        
        // Make GET request to the categories endpoint
        const response = await fetch(API_ENDPOINTS.GET_ALL_CATEGORIES, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${user.access_token}`
          }
        });
        
        // Check if the response is ok
        if (!response.ok) {
          // Create an error with the status code attached so we can check for 401 in the catch block
          const error = new Error(`Server responded with status: ${response.status}`);
          error.status = response.status; // Add status to the error object for use in catch block
          throw error;
        }
        
        // Parse the response data
        const data = await response.json();
        console.log('All categories data:', data);
        
        // Group roles by mainCategory
        const groupedByCategory = data.reduce((acc, role) => {
          // If this mainCategory doesn't exist in our accumulator yet, create it
          if (!acc[role.mainCategory]) {
            acc[role.mainCategory] = {
              category_key: role.mainCategory,
              category_order_index: role.category_order_index,
              // A category is available if at least one role in it is available
              category_available: role.is_available === true,
              roles: []
            };
          } else {
            // Update category availability if this role is available
            if (role.is_available === true) {
              acc[role.mainCategory].category_available = true;
            }
          }
          
          // Add this role to the category
          acc[role.mainCategory].roles.push({
            role: role.subCategory,
            description: role.description,
            is_available: role.is_available,
            category_id: role.category_id
          });
          
          return acc;
        }, {});
        
        // Convert the grouped object to an array
        const categoriesArray = Object.values(groupedByCategory);
        
        // Sort categories: available first, then by category_order_index (ascending)
        const sortedData = [...categoriesArray].sort((a, b) => {
          // First sort by availability
          if (a.category_available && !b.category_available) return -1;
          if (!a.category_available && b.category_available) return 1;
          
          // Then sort by order_index
          if (a.category_order_index === null) return 1;
          if (b.category_order_index === null) return -1;
          return a.category_order_index - b.category_order_index;
        });
        
        setCategories(sortedData);
        
        // Initialize all categories as collapsed
        const initialExpandedState = {};
        sortedData.forEach(item => {
          initialExpandedState[item.category_key] = false;
        });
        setExpandedCategories(initialExpandedState);
        
      } catch (err) {
        console.error('Error fetching categories:', err);
        
        // If it's a 401 error, it's an expired session - log out and redirect to login
        if (err.status === 401 || (err.message && err.message.includes('401'))) {
          console.log('Session expired or unauthorized. Redirecting to login page.');
          logout();
          navigate('/login');
          return;
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchCategories();
  }, [user]);
  
  // Toggle category expansion
  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[100px]"> {/* Added extra top padding for dashboard */}
      <Navigation title="Dashboard" showBackButton={false} />
      
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white p-6 rounded-lg shadow col-span-1 md:col-span-3">
          <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-4">
            Welcome, {user?.name || user?.email || 'User'}!
          </h2>
          <p className="text-gray-600 mb-4">You have successfully logged in and your session is now active.</p>
          
          <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-3">
            Session Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <p className="text-gray-600">ID: <span className="font-semibold text-gray-800">{user?.id}</span></p>          
            <p className="text-gray-600">Email: <span className="font-semibold text-gray-800">{user?.email}</span></p>
            <p className="text-gray-600">Role: <span className="font-semibold text-gray-800">{user?.role}</span></p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow col-span-1 md:col-span-2">
          <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-4">
            Supported Roles
          </h3>
          
          {loading ? (
            <div className="text-center py-4">
              <p className="text-gray-600">Loading roles...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-gray-600">No roles available.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {categories.map((item) => (
                <div key={item.category_key} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleCategory(item.category_key)}
                    className={`w-full flex justify-between items-center p-3 ${
                      !item.category_available
                        ? 'bg-gray-100 text-gray-500' 
                        : 'bg-blue-50 text-blue-700 font-medium'
                    } hover:bg-gray-100 transition-colors text-left`}
                  >
                    <div>
                      <h4 className="font-medium">{item.category_key}</h4>
                      {!item.category_available && (
                        <span className="text-xs text-gray-500">(coming soon)</span>
                      )}
                    </div>
                    <svg 
                      className={`w-5 h-5 transition-transform ${expandedCategories[item.category_key] ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  
                  {expandedCategories[item.category_key] && (
                    <div className="p-3 bg-white">
                      <div className="space-y-2 mt-2">
                        {item.roles.map((role, index) => (
                          <div 
                            key={index} 
                            className={`py-2 px-3 rounded-md text-sm w-full ${
                              role.is_available !== true
                                ? 'bg-gray-100 text-gray-600'
                                : 'bg-blue-50 text-blue-700'
                            }`}
                          >
                            <div className="font-medium">
                              {role.role}
                              {role.is_available !== true && (
                                <span className="text-xs text-gray-500 ml-2">(coming soon)</span>
                              )}
                            </div>
                            {role.description && (
                              <div className="text-xs mt-1 text-blue-400">
                                {role.description}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
